import { Injectable, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { APP_BASE_HREF } from '@angular/common';
import { ReplaySubject, Observable, of, OperatorFunction, pipe } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';
import { UserApplicationPreferencesApi } from '../../../../../api/userApplicationPreferences/userApplicationPreferences.api';
import { UserAppPreferencesModel } from '../../../../../Core/Users/models/user-app-preferences.model';
import { CommonApiErrorModel } from '../../../../../api/genericApi/models/common-api-error.model';
import { CommonApiErrorEnum } from '../../../../../api/genericApi/enums/common-api-error.enum';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { UserApplicationPreferencesApiModel } from '../../../../../api/userApplicationPreferences/models/userApplicationPreferencesApiModel';
import { MessageService } from 'primeng/api';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { cloneDeep } from 'lodash';

export const MAX_FAVORITE_COLORS = 10;
export const FAVORITE_COLORS_SECTION_ID  = 'favoriteColors';

@Injectable({ providedIn: 'root' })
export class FavoriteColorsService {
    private favoriteColors$ = new ReplaySubject<string[]>(1);
    private userAppPrefs: UserAppPreferencesModel | null = null;
    private hasExistingPrefs = true;
    private needForceRefresh = false;

    constructor(
        private userPreferencesService: UserPreferencesService,
        private userAppPrefsApi: UserApplicationPreferencesApi,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        this.userPreferencesService.getUserAppPreferences().pipe(
            switchMap(() => this.userAppPrefsApi.getCurrentUserAppPrefs()),  // to check CommonApiErrorEnum.badReference
            this.preferenceHandlerOperator(),
        ).subscribe(preferences => {
            this.userAppPrefs = preferences;
            this.setFavoriteColors(preferences.generalPrefs?.favoriteColors);
        });
    }

    /**
     * Get the current favorite colors as an observable
     */
    public getFavoriteColors$(): Observable<string[]> {
        return this.favoriteColors$.asObservable();
    }

    public getSyncedFavoriteColors$(): Observable<string[]> {
        if (this.needForceRefresh) {
            return this.userAppPrefsApi.getCurrentUserAppPrefs().pipe(
                this.preferenceHandlerOperator(),
                tap((preferences) => {
                    this.needForceRefresh = false;
                    this.userAppPrefs = preferences;
                    this.setFavoriteColors(preferences.generalPrefs?.favoriteColors);
                }),
                map(() => this.getFavoriteColorsSync())
            );
        } else {
            return of(this.getFavoriteColorsSync());
        }
    }

    /**
     * Set all favorite colors at once
     * @param colors Array of hex color strings
    */
    public saveFavoriteColors(colors: string[]): void {
        const maximumColors = (colors || []).slice(0, MAX_FAVORITE_COLORS).map(c => c.toUpperCase());
        this.updateUserPreference(maximumColors);
    }

    public openUserPreferencePage() {
        this.needForceRefresh = true;
        window.open(this.appBaseHref + `UserPreferences?#${FAVORITE_COLORS_SECTION_ID}`, '_blank');
    }

    private preferenceHandlerOperator(): OperatorFunction<UserApplicationPreferencesApiModel, UserAppPreferencesModel> {
        return pipe(
            map((apiModel) => new UserAppPreferencesModel(apiModel)),
            catchError((error: HttpErrorResponse) => {
                if (CommonApiErrorModel.getErrorDetail(error.error)?.code ===
                    CommonApiErrorEnum.badReference
                ) {
                    this.hasExistingPrefs = false;
                }

                return of(new UserAppPreferencesModel());
            })
        );
    }

    private setFavoriteColors(colors?: string[]) {
        this.favoriteColors$.next(colors ?? []);
    }


    private updateUserPreference(colors: string[]) {
        if (!this.userAppPrefs) {
            this.userAppPrefs = new UserAppPreferencesModel();
        }

        const clonedModel = cloneDeep(this.userAppPrefs);
        clonedModel.generalPrefs.favoriteColors = colors;

        const apiModel = clonedModel.toApiModel();
        let saveUserPreference$ = null;

        if (this.hasExistingPrefs) {
            saveUserPreference$ = this.userAppPrefsApi.updateCurrentUserAppPrefs(apiModel).pipe(
                tap(() => this.hasExistingPrefs = true)
            );
        } else {
            saveUserPreference$ =  this.userAppPrefsApi.createCurrentUserAppPrefs(apiModel).pipe(
                tap(() => this.hasExistingPrefs = true)
            );
        }

        saveUserPreference$.subscribe({
            next: () => {
                this.userAppPrefs!.generalPrefs.favoriteColors = colors;
                this.setFavoriteColors(colors);
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'ColorPicker.favoriteColors.save.success.heading',
                    detail: 'ColorPicker.favoriteColors.save.success.general.message'
                });
            },
            error: (error) => {
                console.error('Failed to create favorite colors', error);
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'ColorPicker.favoriteColors.save.fail.heading',
                    detail: 'ColorPicker.favoriteColors.save.fail.general.message'
                });
            }
        });
    }

    /**
    * Get the current favorite colors synchronously (for use when already initialized)
    */
    private getFavoriteColorsSync(): string[] {
        return this.userAppPrefs?.generalPrefs?.favoriteColors ?? [];
    }
}
